import { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const projects = [
  {
    id: 1,
    title: "Kitchen Remodel",
    location: "Santaquin, UT",
    description: "Complete kitchen renovation with black shaker cabinets, granite countertops, and new appliances",
    image: "https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/1vf4qovi_kiten%20remodel.jpg",
    category: "remodel"
  },
  {
    id: 2,
    title: "Bathroom Remodel - Before",
    location: "Utah County",
    description: "Original bathroom before renovation - outdated shower and damaged tile flooring",
    image: "https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/z3isznm0_Before...png",
    category: "before"
  },
  {
    id: 3,
    title: "Bathroom Remodel - After",
    location: "Utah County",
    description: "Beautiful transformation with marble-look tile, decorative accent strip, and modern fixtures",
    image: "https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/bvhv8h1z_Before..%20%281%29.png",
    category: "after"
  },
  {
    id: 4,
    title: "Concrete Finishing",
    location: "Santaquin Area",
    description: "Professional concrete finishing with smooth swirl patterns",
    image: "https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/5p61jur3_Untitled%20design%20%2815%29.png",
    category: "concrete"
  },
  {
    id: 5,
    title: "Concrete Pour",
    location: "Utah County",
    description: "Backyard concrete slab pour with rebar reinforcement and mountain views",
    image: "https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/e9atq3yb_Untitled%20design%20%2816%29.png",
    category: "concrete"
  },
  {
    id: 6,
    title: "Drywall Installation",
    location: "Utah County",
    description: "Interior drywall with arched doorway and recessed lighting prep",
    image: "https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/g94jush8_Untitled%20design%20%2817%29.png",
    category: "drywall"
  }
];

export default function GallerySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (project, index) => {
    setSelectedImage(project);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % projects.length;
    setCurrentIndex(newIndex);
    setSelectedImage(projects[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + projects.length) % projects.length;
    setCurrentIndex(newIndex);
    setSelectedImage(projects[newIndex]);
  };

  return (
    <section
      id="gallery"
      className="py-24 md:py-32 bg-[#FAFAF9]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57534E] mb-4 block">
            Our Work
          </span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-[#1C1917] mb-4">
            Recent Projects
          </h2>
          <p className="text-[#57534E] max-w-2xl mx-auto">
            Real projects from real customers. See the quality craftsmanship we bring to every job.
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group cursor-pointer"
              onClick={() => openLightbox(project, index)}
              data-testid={`gallery-item-${project.id}`}
            >
              <div className="relative overflow-hidden aspect-[4/3] bg-[#F5F5F4]">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <p className="text-xs uppercase tracking-wider text-white/70 mb-1">
                    {project.location}
                  </p>
                  <h3 className="font-['Outfit'] text-xl font-medium text-white">
                    {project.title}
                  </h3>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Placeholder for more projects - only show if less than 8 */}
          {projects.length < 8 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="aspect-[4/3] bg-[#F5F5F4] border-2 border-dashed border-[#E7E5E4] flex items-center justify-center"
            >
              <div className="text-center p-6">
                <p className="text-[#57534E] text-sm">More projects coming soon</p>
                <p className="text-[#A8A29E] text-xs mt-1">We're always working on something new</p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Call to action */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-[#57534E] mt-12"
        >
          Want to see your project featured here?{" "}
          <button
            onClick={() => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" })}
            className="text-[#C2410C] font-medium hover:underline"
          >
            Get a free quote today
          </button>
        </motion.p>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-[#1C1917]/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>

          {projects.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 md:left-8 text-white/70 hover:text-white transition-colors p-2"
              >
                <ChevronLeft size={40} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 md:right-8 text-white/70 hover:text-white transition-colors p-2"
              >
                <ChevronRight size={40} />
              </button>
            </>
          )}

          <div
            className="max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="w-full max-h-[70vh] object-contain"
            />
            <div className="mt-6 text-center">
              <h3 className="font-['Outfit'] text-2xl font-medium text-white mb-2">
                {selectedImage.title}
              </h3>
              <p className="text-white/70 text-sm mb-2">{selectedImage.location}</p>
              <p className="text-white/60 max-w-2xl mx-auto">
                {selectedImage.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
